import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cn } from '../utils/cn';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  asChild?: boolean;
}

const base =
  'inline-flex items-center justify-center gap-2 font-medium select-none ' +
  'whitespace-nowrap transition-[background-color,color,border-color,box-shadow] ' +
  'duration-150 ease-out disabled:opacity-50 disabled:cursor-not-allowed ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-petrol/40 ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-bg';

const variantClasses: Record<Variant, string> = {
  // Explicit text-white on dark surfaces so disabled+dark never collapses to
  // "ink on ink".
  primary:
    'bg-petrol text-white hover:bg-petrol-deep border border-petrol ' +
    'disabled:bg-petrol disabled:text-white',
  secondary:
    'bg-surface text-ink border border-rule-strong hover:border-ink hover:bg-surface-tint',
  ghost: 'bg-transparent text-ink hover:bg-surface-tint border border-transparent',
  danger:
    'bg-clay text-white hover:bg-clay-deep border border-clay ' +
    'disabled:bg-clay disabled:text-white',
  link: 'bg-transparent text-petrol underline-offset-4 hover:underline px-0 py-0 border-0 h-auto',
};

const sizeClasses: Record<Size, string> = {
  sm: 'h-8 px-3 text-sm rounded-md',
  md: 'h-10 px-4 text-[15px] rounded-md',
  lg: 'h-12 px-5 text-base rounded-md',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'primary', size = 'md', asChild = false, type, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref as never}
        type={asChild ? undefined : (type ?? 'button')}
        className={cn(
          base,
          variantClasses[variant],
          variant !== 'link' && sizeClasses[size],
          className,
        )}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';
